'use client'

import { useState, useEffect, useCallback } from 'react'
import Image from 'next/image'
import { motion, AnimatePresence } from 'framer-motion'
import { ExternalLink, ChevronLeft, ChevronRight, ChevronDown, Star, Sparkles } from 'lucide-react'

interface Project {
  id: string
  name: string
  category: string
  description: string
  image: string
  fullImage: string
  url: string
  badge?: 'destaque' | 'novo' | 'premium'
}

const projects: Project[] = [
  {
    id: 'liboni-bauer',
    name: 'Liboni Bauer',
    category: 'Marketing Digital',
    description: 'Site institucional com design premium e SEO otimizado',
    image: '/portfolio/slide-liboni-bauer.webp',
    fullImage: '/portfolio/liboni-bauer.webp',
    url: 'https://libonibauer.com.br'
  },
  {
    id: 'tupa-fc',
    name: 'Tupa FC',
    category: 'Esportes',
    description: 'Portal completo para clube de futebol profissional',
    image: '/portfolio/tupa-fc.webp',
    fullImage: '/portfolio/tupa-fc.webp',
    url: 'https://tupafc.com.br'
  },
  {
    id: 'orestes',
    name: 'Orestes Cabeleireiro',
    category: 'Beleza & Estética',
    description: 'Landing page moderna com sistema de agendamento',
    image: '/portfolio/orestes-cabeleireiro.webp',
    fullImage: '/portfolio/orestes-cabeleireiro.webp',
    url: 'https://orestescabeleireiro.com.br'
  }
]

const badgeConfig = {
  destaque: {
    label: 'Destaque',
    icon: Star,
    className: 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
  },
  novo: {
    label: 'Novo',
    icon: Sparkles,
    className: 'bg-purple-500 text-white'
  },
  premium: {
    label: 'Premium',
    icon: Star,
    className: 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
  }
}

interface PortfolioShowcaseProps {
  variant?: 'compact' | 'full' | 'hero'
  autoPlayInterval?: number
}

export function PortfolioShowcase({
  variant = 'compact',
  autoPlayInterval = 5000
}: PortfolioShowcaseProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isHovered, setIsHovered] = useState(false)
  const [direction, setDirection] = useState(0)

  const nextSlide = useCallback(() => {
    setDirection(1)
    setCurrentIndex((prev) => (prev + 1) % projects.length)
  }, [])

  const prevSlide = useCallback(() => {
    setDirection(-1)
    setCurrentIndex((prev) => (prev - 1 + projects.length) % projects.length)
  }, [])

  useEffect(() => {
    if (isHovered) return
    const interval = setInterval(nextSlide, autoPlayInterval)
    return () => clearInterval(interval)
  }, [nextSlide, isHovered, autoPlayInterval])

  const currentProject = projects[currentIndex]
  const badge = currentProject.badge ? badgeConfig[currentProject.badge] : null

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0,
      scale: 0.95
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 100 : -100,
      opacity: 0,
      scale: 0.95
    })
  }

  if (variant === 'compact') {
    return (
      <div
        className="relative w-full"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Main showcase container */}
        <div className="relative rounded-2xl overflow-hidden bg-white/50 dark:bg-black/40 border border-purple-500/20 dark:border-white/10">
          {/* Screenshot area - scrollable to show full site */}
          <div className="relative h-[220px] overflow-hidden">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                className="absolute inset-0"
              >
                {/* Scrollable screenshot container */}
                <div className="relative h-full overflow-y-auto scrollbar-hide group/scroll">
                  <Image
                    src={currentProject.fullImage}
                    alt={currentProject.name}
                    width={800}
                    height={1200}
                    className="w-full object-cover object-top"
                    sizes="(max-width: 768px) 100vw, 400px"
                    loading="lazy"
                  />

                  {/* Scroll indicator */}
                  <div className="sticky bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-black/90 to-transparent flex items-center justify-center pointer-events-none">
                    <motion.div
                      animate={{ y: [0, 4, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      <ChevronDown size={16} className="text-white/60" />
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Badge */}
            {badge && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`absolute top-3 left-3 z-20 flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold ${badge.className}`}
              >
                <badge.icon size={10} />
                {badge.label}
              </motion.div>
            )}

            {/* Category badge */}
            <div className="absolute top-3 right-3 z-20 px-2 py-1 rounded-full bg-black/60 backdrop-blur-sm text-white/80 text-[10px] font-medium">
              {currentProject.category}
            </div>

            {/* Navigation arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-black/70 transition-all opacity-0 group-hover:opacity-100"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-black/70 transition-all opacity-0 group-hover:opacity-100"
            >
              <ChevronRight size={16} />
            </button>

            {/* Hover overlay with CTA */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-12 pointer-events-none">
              <motion.a
                href={currentProject.url}
                target="_blank"
                rel="noopener noreferrer"
                className="pointer-events-auto flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500 hover:bg-purple-400 text-white text-sm font-medium transition-colors shadow-lg shadow-purple-500/30"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Visitar Site <ExternalLink size={14} />
              </motion.a>
            </div>
          </div>

          {/* Project info */}
          <div className="p-3 border-t border-purple-500/10 dark:border-white/5">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-center justify-between mb-1">
                  <h4 className="text-indigo-950 dark:text-white font-semibold text-sm">{currentProject.name}</h4>
                  <a
                    href={currentProject.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-500 dark:text-purple-400 hover:text-purple-600 dark:hover:text-purple-300 transition-colors"
                  >
                    <ExternalLink size={14} />
                  </a>
                </div>
                <p className="text-indigo-950/50 dark:text-white/50 text-xs line-clamp-1">{currentProject.description}</p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Indicators */}
        <div className="flex items-center justify-center gap-3 mt-3">
          <span className="text-indigo-950/30 dark:text-white/30 text-xs font-medium tabular-nums">
            {currentIndex + 1}/{projects.length}
          </span>
          <div className="flex gap-1.5">
            {projects.map((project, index) => (
              <button
                key={project.id}
                onClick={() => {
                  setDirection(index > currentIndex ? 1 : -1)
                  setCurrentIndex(index)
                }}
                className={`rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'bg-purple-500 w-5 h-1.5'
                    : 'bg-indigo-950/20 dark:bg-white/20 hover:bg-indigo-950/30 dark:hover:bg-white/30 w-1.5 h-1.5'
                }`}
                aria-label={`Ver ${project.name}`}
              />
            ))}
          </div>
        </div>
      </div>
    )
  }

  // Hero variant - Clean preview for floating display
  if (variant === 'hero') {
    return (
      <div
        className="relative w-full"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Site Preview - Hero only */}
        <div className="relative overflow-hidden aspect-[16/9] bg-black">
          <AnimatePresence initial={false}>
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: 'easeInOut' }}
              className="absolute inset-0"
            >
              <Image
                src={currentProject.fullImage}
                alt={currentProject.name}
                fill
                className="object-cover object-top"
                sizes="(max-width: 768px) 100vw, 620px"
                priority
              />
            </motion.div>
          </AnimatePresence>

          {/* Navigation arrows - subtle */}
          <button
            onClick={prevSlide}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white/60 hover:text-white hover:bg-black/60 transition-all opacity-0 group-hover:opacity-100"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white/60 hover:text-white hover:bg-black/60 transition-all opacity-0 group-hover:opacity-100"
          >
            <ChevronRight size={16} />
          </button>

          {/* Badge */}
          {badge && (
            <div className={`absolute top-2 left-2 z-20 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge.className}`}>
              <badge.icon size={10} />
              {badge.label}
            </div>
          )}
        </div>

        {/* Compact Info Bar */}
        <div className="p-2.5 bg-indigo-950/10 dark:bg-zinc-800/60">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="flex items-center justify-between"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-indigo-950 dark:text-white text-sm font-medium truncate">{currentProject.name}</span>
                <span className="text-purple-500/80 dark:text-purple-400/80 text-[10px] px-1.5 py-0.5 rounded bg-purple-500/10">
                  {currentProject.category}
                </span>
              </div>
              <a
                href={currentProject.url}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 text-indigo-950/50 dark:text-white/50 hover:text-purple-500 dark:hover:text-purple-400 transition-colors"
              >
                <ExternalLink size={14} />
              </a>
            </motion.div>
          </AnimatePresence>

          {/* Indicators */}
          <div className="flex items-center justify-center gap-1.5 mt-2">
            {projects.map((project, index) => (
              <button
                key={project.id}
                onClick={() => {
                  setDirection(index > currentIndex ? 1 : -1)
                  setCurrentIndex(index)
                }}
                className={`rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'bg-purple-500 w-4 h-1'
                    : 'bg-indigo-950/20 dark:bg-white/20 hover:bg-indigo-950/30 dark:hover:bg-white/30 w-1 h-1'
                }`}
                aria-label={`Ver ${project.name}`}
              />
            ))}
          </div>
        </div>
      </div>
    )
  }

  // Full variant - for larger displays or dedicated sections
  return (
    <div
      className="relative w-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="grid md:grid-cols-3 gap-4">
        {projects.map((project, index) => {
          const projectBadge = project.badge ? badgeConfig[project.badge] : null

          return (
            <motion.a
              key={project.id}
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="group relative rounded-2xl overflow-hidden bg-black/40 border border-white/10 hover:border-purple-500/30 transition-all"
            >
              {/* Screenshot */}
              <div className="relative h-[200px] overflow-y-auto scrollbar-hide">
                <Image
                  src={project.fullImage}
                  alt={project.name}
                  width={600}
                  height={900}
                  className="w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  sizes="(max-width: 768px) 100vw, 33vw"
                  loading="lazy"
                />

                {/* Scroll hint */}
                <div className="sticky bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-black/80 to-transparent flex items-center justify-center pointer-events-none">
                  <ChevronDown size={12} className="text-white/50 animate-bounce" />
                </div>
              </div>

              {/* Badge */}
              {projectBadge && (
                <div className={`absolute top-2 left-2 z-10 flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${projectBadge.className}`}>
                  <projectBadge.icon size={8} />
                  {projectBadge.label}
                </div>
              )}

              {/* Category */}
              <div className="absolute top-2 right-2 z-10 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-sm text-white/70 text-[9px]">
                {project.category}
              </div>

              {/* Visit button on hover */}
              <div className="absolute bottom-14 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-purple-500 text-white text-[10px] font-medium shadow-lg">
                  Visitar <ExternalLink size={10} />
                </span>
              </div>

              {/* Info */}
              <div className="p-3 border-t border-white/5">
                <h4 className="text-white font-semibold text-sm mb-0.5">{project.name}</h4>
                <p className="text-white/40 text-xs line-clamp-1">{project.description}</p>
              </div>
            </motion.a>
          )
        })}
      </div>
    </div>
  )
}
